import { memo } from 'react'
import { usePress } from '@react-aria/interactions'
import cn from 'classnames'

import { HStack } from '~/shared/ui/HStack'
import { Text } from '~/shared/ui/Text'

import { STEPPER_BUTTON_SIZE, STEPPER_VALUE_MIN_WIDTH } from './config'
import type { StepperProps } from './types'

import styles from './Stepper.module.scss'

const StepButton = memo(function StepButton(props: {
  glyph: string
  onPress: () => void
  isDisabled: boolean
  ariaLabel: string
}) {
  const { glyph, onPress, isDisabled, ariaLabel } = props
  const { pressProps, isPressed } = usePress({ onPress, isDisabled })

  return (
    <button
      className={cn(styles.button, isDisabled && styles.isDisabled)}
      type='button'
      aria-label={ariaLabel}
      aria-disabled={isDisabled || undefined}
      data-pressed={isPressed}
      style={{ width: STEPPER_BUTTON_SIZE, height: STEPPER_BUTTON_SIZE }}
      {...pressProps}
    >
      <Text variant='H3' color='INHERIT'>
        {glyph}
      </Text>
    </button>
  )
})

/** Степпер число ± по спеке 01 §29: кнопки 32×32, значение по центру H3. */
export const Stepper = memo(function Stepper(props: StepperProps) {
  const { value, onChange, min = -Infinity, max = Infinity, step = 1 } = props

  const canDecrement = value > min
  const canIncrement = value < max

  return (
    <HStack gap='MEDIUM_XS' align='CENTER'>
      <StepButton
        glyph='−'
        ariaLabel='Уменьшить'
        isDisabled={!canDecrement}
        onPress={() => onChange(Math.max(min, value - step))}
      />
      <span className={styles.value} style={{ minWidth: STEPPER_VALUE_MIN_WIDTH }}>
        <Text variant='H3' align='CENTER'>
          {value}
        </Text>
      </span>
      <StepButton
        glyph='+'
        ariaLabel='Увеличить'
        isDisabled={!canIncrement}
        onPress={() => onChange(Math.min(max, value + step))}
      />
    </HStack>
  )
})
